import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';
import { FormattedMessage } from 'react-intl';
import 'Components/Utility/InfiniteMore.scss';

// Like infinite scrolling, but with a load more button instead of scroll event detection.

const InfiniteMore = ({
  children,
  loadMore,
  pageStart,
  perPage,
  hasMore,
  showMoreNumber,
  initialLoad,
  loader,
  filter,
}) => {
  const [page, setPage] = useState(pageStart);
  const [isLoading, setIsLoading] = useState(false);

  const doLoad = useCallback(async () => {
    setIsLoading(true);
    await loadMore(page + 1, perPage);
    setIsLoading(false);
    setPage(prevPage => prevPage + 1);
  }, [loadMore, page, perPage]);

  useEffect(() => {
    if (initialLoad && hasMore) {
      doLoad();
    }
  }, [initialLoad, hasMore, doLoad]);

  useEffect(() => {
    setPage(0);
  }, [filter]);

  return (
    <div className="infinite-more-container">
      {children}
      {!isLoading && hasMore && filter !== 'e_books' && (
        <div className="infinite-more" key={0}>
          <Button color="primary" size="lg" onClick={doLoad}>
            {showMoreNumber ? (
              <b>
                <FormattedMessage
                  id="infinite-more.loading"
                  defaultMessage="Load {perPage} More..."
                  values={{ perPage }}
                />
              </b>
            ) : (
              <b>
                <FormattedMessage id="infinite-more.loading-load" defaultMessage="Load More..." />
              </b>
            )}
          </Button>
        </div>
      )}
      {isLoading && (
        <div className="infinite-more-loader-wrap">
          {loader}
        </div>
      )}
    </div>
  );
};

InfiniteMore.propTypes = {
  children: PropTypes.node.isRequired, // Child Elements
  loadMore: PropTypes.func.isRequired, // Async load more function, passed perPage and currentPage
  pageStart: PropTypes.number, // Page number to start on
  perPage: PropTypes.number, // Results to load per page (passed to load more)
  hasMore: PropTypes.bool, // Are there any more results to come?
  showMoreNumber: PropTypes.bool, // Show perPage number in more button?
  initialLoad: PropTypes.bool, // Run loadMore function on render
  loader: PropTypes.node, // Loading animation component
  filter: PropTypes.string,
};

InfiniteMore.defaultProps = {
  perPage: 25,
  pageStart: 0,
  hasMore: true,
  showMoreNumber: false,
  initialLoad: true,
  loader: (
    <div className="infinite-loader" key={0}>
      <div className="spinner">
        <div className="rect1" />
        <div className="rect2" />
        <div className="rect3" />
        <div className="rect4" />
        <div className="rect5" />
      </div>
    </div>
  ),
  filter: 'top_sellers',
};

export default InfiniteMore;
